/**
 * Task 17 — Default deposits card (Admin System Settings).
 *
 * Lets super-admins configure the tenant-wide default security deposits
 * keyed by equipment type. Admins see a read-only view (PUT 403s).
 *
 * Data shape (server-side):
 *   GET  /api/admin/settings/default-deposits
 *     → { defaults: { reefer: 200000, ... }, equipment_types: [...] }
 *   PUT  /api/admin/settings/default-deposits
 *     ← { defaults: { reefer: 200000, ... } }
 *
 * Amounts are stored as integer cents to keep parity with rental.deposit.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EQUIPMENT_LABELS = {
    reefer: "Reefer",
    dry_van: "Dry Van",
    conestoga: "Conestoga",
    flatbed: "Flatbed",
    step_deck: "Step Deck",
    other: "Other",
};

function centsToInput(cents) {
    if (cents == null || cents === 0) return "";
    return String((Number(cents) || 0) / 100);
}

function inputToCents(s) {
    if (s == null || String(s).trim() === "") return 0;
    const n = Number(String(s).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(n) || n < 0) return NaN;
    return Math.round(n * 100);
}

export function DefaultDepositsCard({ isSuperAdmin }) {
    const [defaults, setDefaults] = useState({});
    const [equipmentTypes, setEquipmentTypes] = useState(Object.keys(EQUIPMENT_LABELS));
    const [draft, setDraft] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/settings/default-deposits");
            setDefaults(data?.defaults || {});
            const slugs = Array.isArray(data?.equipment_types) && data.equipment_types.length
                ? data.equipment_types : Object.keys(EQUIPMENT_LABELS);
            setEquipmentTypes(slugs);
            const next = {};
            for (const s of slugs) next[s] = centsToInput((data?.defaults || {})[s]);
            setDraft(next);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const isDirty = () => {
        for (const s of equipmentTypes) {
            const persisted = centsToInput(defaults[s]);
            if ((draft[s] || "") !== (persisted || "")) return true;
        }
        return false;
    };

    const onSave = async () => {
        const payload = {};
        for (const slug of equipmentTypes) {
            const raw = draft[slug];
            if (raw == null || String(raw).trim() === "") continue;
            const cents = inputToCents(raw);
            if (!Number.isFinite(cents) || cents < 0) {
                toast.error(`Enter a valid amount for ${EQUIPMENT_LABELS[slug] || slug}`);
                return;
            }
            if (cents > 100_000_000) {
                toast.error(`${EQUIPMENT_LABELS[slug] || slug}: max is $1,000,000`);
                return;
            }
            if (cents > 0) payload[slug] = cents;
        }
        setSaving(true);
        try {
            const { data } = await api.put("/admin/settings/default-deposits", { defaults: payload });
            setDefaults(data?.defaults || {});
            const next = {};
            for (const s of equipmentTypes) next[s] = centsToInput((data?.defaults || {})[s]);
            setDraft(next);
            toast.success("Default deposits saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card data-testid="settings-default-deposits-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">Default security deposits</CardTitle>
                </div>
                <CardDescription>
                    Suggested deposit amount per equipment type. Drives the soft warning at
                    rental edit and the hard-confirm gate at contract send. Admins can still
                    override on a per-rental basis.
                    {!isSuperAdmin && (
                        <span className="block mt-1 text-xs text-amber-700">
                            Read-only — only super admins can edit these.
                        </span>
                    )}
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="space-y-3" data-testid="settings-default-deposits-list">
                    {equipmentTypes.map((slug) => (
                        <div key={slug} className="flex items-center gap-3" data-testid={`settings-default-deposits-row-${slug}`}>
                            <Label className="w-28 text-sm text-slate-700">{EQUIPMENT_LABELS[slug] || slug}</Label>
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-sm">$</span>
                                <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    disabled={loading || saving || !isSuperAdmin}
                                    value={draft[slug] ?? ""}
                                    onChange={(e) =>
                                        setDraft((prev) => ({ ...prev, [slug]: e.target.value }))
                                    }
                                    className="w-36"
                                    data-testid={`settings-default-deposits-input-${slug}`}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                {isSuperAdmin && (
                    <div className="mt-5 flex items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                            Leave blank to clear a type. Values stored in cents server-side.
                        </p>
                        <Button
                            onClick={onSave}
                            disabled={loading || saving || !isDirty()}
                            className="bg-blue-900 hover:bg-blue-800"
                            data-testid="settings-default-deposits-save"
                        >
                            {saving ? "Saving…" : "Save defaults"}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export default DefaultDepositsCard;
